import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, TextField, Button, Grid,
  Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel,
  IconButton, Accordion, AccordionSummary, AccordionDetails, Chip, Alert,
  RadioGroup, Radio
} from '@mui/material';
import { Add, Remove, ExpandMore, PhotoCamera, Save, Send, Delete } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/AppHeader';
import axios from 'axios';
import ImageUploader from '../../components/ImageUploader';

const API_URL = process.env.REACT_APP_API_URL || '';

const GERAETETYPEN = ['Wandgerät', 'Truhengerät', 'Kassettengerät', 'Kanalgerät'];
const HERSTELLER = ['Panasonic', 'Kaysun', 'Daikin', 'Mitsubishi', 'Toshiba', 'LG', 'Samsung', 'Sinclair'];
const MONTAGE_AUF = ['Wandkonsole', 'Bigfoot', 'Streifenfundament', 'Dachtritt'];
const ISOLIERSTANDARD = ['neubau', 'altbau', 'dach', 'wintergarten'];

export default function AngebotFormPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const customer = location.state?.customer;
  const existingAngebot = location.state?.existingAngebot;

  const [angebotId, setAngebotId] = useState(existingAngebot?._id || null);
  const [monteur, setMonteur] = useState(user?.full_name || '');
  const [datum, setDatum] = useState(new Date().toISOString().split('T')[0]);

  // Helper function: Auto-capitalize first letter
  const capitalize = (value) => {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  // Global keyboard handler for checkboxes
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if Enter is pressed and target is a checkbox input
      if (e.key === 'Enter' && e.target.type === 'checkbox') {
        e.preventDefault();
        e.target.click(); // Toggle checkbox
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  const [herstellerWunsch, setHerstellerWunsch] = useState('');
  const [herstellerEmpfehlung, setHerstellerEmpfehlung] = useState('');
  const [serviceart, setServiceart] = useState('Komplettmontage');
  const [montageGeplant, setMontageGeplant] = useState('sofort');
  
  const [raeume, setRaeume] = useState([createEmptyRaum()]);
  const [aussengeraete, setAussengeraete] = useState([createEmptyAussengeraet()]);
  
  const [kernbohrungen, setKernbohrungen] = useState('');
  const [bohrungen, setBohrungen] = useState('');
  const [mauerschlitz, setMauerschlitz] = useState(''); // NEU
  const [notizen, setNotizen] = useState('');
  const [bilder, setBilder] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Accordion State: Track welche Accordions offen sind
  const [expandedRaeume, setExpandedRaeume] = useState(new Set([0])); // Erster Raum offen
  const [expandedAussengeraete, setExpandedAussengeraete] = useState(new Set([0])); // Erstes Außengerät offen

  // Load existing angebot data if editing
  useEffect(() => {
    if (existingAngebot) {
      setAngebotId(existingAngebot._id);
      setMonteur(existingAngebot.monteur || '');
      setDatum(existingAngebot.datum || new Date().toISOString().split('T')[0]);
      setHerstellerWunsch(existingAngebot.hersteller_wunsch || '');
      setHerstellerEmpfehlung(existingAngebot.hersteller_empfehlung || '');
      setServiceart(existingAngebot.serviceart || 'Komplettmontage');
      setMontageGeplant(existingAngebot.montage_geplant || 'sofort');
      setRaeume(existingAngebot.raeume && existingAngebot.raeume.length > 0 ? existingAngebot.raeume : [createEmptyRaum()]);
      setAussengeraete(existingAngebot.aussengeraete && existingAngebot.aussengeraete.length > 0 ? existingAngebot.aussengeraete : [createEmptyAussengeraet()]);
      setKernbohrungen(existingAngebot.kernbohrungen || '');
      setBohrungen(existingAngebot.bohrungen || '');
      setMauerschlitz(existingAngebot.mauerschlitz || '');
      setNotizen(existingAngebot.notizen || '');
      setBilder(existingAngebot.bilder || []);
    }
  }, [existingAngebot]);

  function createEmptyRaum() {
    return {
      raumname: '',
      laenge: '',
      breite: '',
      hoehe: '',
      isolierstandard: 'neubau', // NEU
      kuehlleistung_kw: 0, // NEU
      besonderheiten: '',
      geraetetyp: 'Wandgerät',
      kondenswasserpumpe: false,
      wifi: false,
      wunschfarbe: '',
      leitungslaenge_innen_aussen: ''
    };
  }

  function createEmptyAussengeraet() {
    return {
      typ: 'Single',
      montageort: 'Wand',
      montage_auf: '', // NEU: Auch bei Single anzeigen
      hoehe: '',
      zugeordnete_raeume: [],
      dachtritt_vorhanden: false,
      kondenswasserwanne: false,
      oelauffangwanne: false,
      einhausung: false,
      ablaufheizung: false,
      vollwaermeschutz: false,
      vollwaermeschutz_staerke_mm: '',
      montagelift: false,
      arbeitsbuehne: false,
      dachlift: false
    };
  }

  const parseNumber = (value) => {
    if (!value) return 0;
    const str = String(value).replace(',', '.');
    return parseFloat(str) || 0;
  };

  // Kühllast-Berechnung (pro m³)
  const calculateKuehlleistung = (raum) => {
    const volumen = parseNumber(raum.breite) * parseNumber(raum.laenge) * parseNumber(raum.hoehe);
    if (volumen === 0) return 0;
    
    // Watt pro m³ je nach Isolierstandard
    const wattProKubikmeter = {
      'neubau': 30,      // 30 W/m³
      'altbau': 45,      // 45 W/m³
      'dach': 65,        // 65 W/m³
      'wintergarten': 85 // 85 W/m³
    };
    
    const watt = wattProKubikmeter[raum.isolierstandard] || 45; // Default: Altbau
    const kuehllast = (volumen * watt) / 1000; // in kW
    return kuehllast.toFixed(2);
  };

  const calculateFlaeche = (raum) => {
    const breite = parseNumber(raum.breite);
    const laenge = parseNumber(raum.laenge);
    return (breite * laenge).toFixed(2);
  };

  const calculateVolumen = (raum) => {
    const breite = parseNumber(raum.breite);
    const laenge = parseNumber(raum.laenge);
    const hoehe = parseNumber(raum.hoehe);
    return (breite * laenge * hoehe).toFixed(2);
  };

  const addRaum = () => {
    const newIndex = raeume.length;
    setRaeume([...raeume, createEmptyRaum()]);
    
    // Öffne das neue Accordion
    setExpandedRaeume(new Set([...expandedRaeume, newIndex]));
    
    // Fokussiere das erste Feld nach kurzer Verzögerung
    setTimeout(() => {
      const input = document.querySelector(`#raum-${newIndex}-raumname`);
      if (input) input.focus();
    }, 100);
  };

  const handleRaumAccordionChange = (index) => (event, isExpanded) => {
    const newExpanded = new Set(expandedRaeume);
    if (isExpanded) {
      newExpanded.add(index);
    } else {
      newExpanded.delete(index);
    }
    setExpandedRaeume(newExpanded);
  };

  const handleAussengeraetAccordionChange = (index) => (event, isExpanded) => {
    const newExpanded = new Set(expandedAussengeraete);
    if (isExpanded) {
      newExpanded.add(index);
    } else {
      newExpanded.delete(index);
    }
    setExpandedAussengeraete(newExpanded);
  };
  const removeRaum = (index) => {
    // Entferne Raum auch aus allen Zuordnungen
    setAussengeraete(aussengeraete.map(ag => ({
      ...ag,
      zugeordnete_raeume: ag.zugeordnete_raeume.filter(i => i !== index).map(i => i > index ? i - 1 : i)
    })));
    setRaeume(raeume.filter((_, i) => i !== index));
    
    // Update accordion state: remove deleted index and adjust higher indices
    const newExpanded = new Set();
    expandedRaeume.forEach(expandedIndex => {
      if (expandedIndex < index) {
        newExpanded.add(expandedIndex);
      } else if (expandedIndex > index) {
        newExpanded.add(expandedIndex - 1);
      }
      // Skip the deleted index
    });
    setExpandedRaeume(newExpanded);
  };

  const addAussengeraet = () => {
    const newIndex = aussengeraete.length;
    setAussengeraete([...aussengeraete, createEmptyAussengeraet()]);
    
    // Öffne das neue Accordion
    setExpandedAussengeraete(new Set([...expandedAussengeraete, newIndex]));
    
    // Fokussiere das erste Feld
    setTimeout(() => {
      const input = document.querySelector(`#aussengeraet-${newIndex}-montageort`);
      if (input) input.focus();
    }, 100);
  };
  const removeAussengeraet = (index) => {
    setAussengeraete(aussengeraete.filter((_, i) => i !== index));
    
    // Update accordion state: remove deleted index and adjust higher indices
    const newExpanded = new Set();
    expandedAussengeraete.forEach(expandedIndex => {
      if (expandedIndex < index) {
        newExpanded.add(expandedIndex);
      } else if (expandedIndex > index) {
        newExpanded.add(expandedIndex - 1);
      }
      // Skip the deleted index
    });
    setExpandedAussengeraete(newExpanded);
  };

  const updateRaum = (index, field, value) => {
    const updated = [...raeume];
    updated[index][field] = value;
    
    // Auto-berechne Kühllast wenn Isolierstandard oder Dimensionen ändern
    if (field === 'isolierstandard' || field === 'breite' || field === 'laenge' || field === 'hoehe') {
      updated[index].kuehlleistung_kw = parseFloat(calculateKuehlleistung(updated[index]));
    }
    
    setRaeume(updated);
  };

  const updateAussengeraet = (index, field, value) => {
    const updated = [...aussengeraete];
    updated[index][field] = value;
    setAussengeraete(updated);
  };

  // NEU: Verfügbare Räume für Zuordnung (exkludiere bereits zugeordnete)
  const getAvailableRaeume = (currentAgIndex) => {
    const alreadyAssigned = aussengeraete
      .filter((ag, idx) => idx !== currentAgIndex)
      .flatMap(ag => ag.zugeordnete_raeume);
    
    return raeume.map((raum, idx) => ({
      index: idx,
      name: raum.raumname || `Raum ${idx + 1}`,
      disabled: alreadyAssigned.includes(idx)
    }));
  };

  const toggleRaumZuordnung = (agIndex, raumIndex) => {
    const updated = [...aussengeraete];
    const current = updated[agIndex].zugeordnete_raeume;
    
    if (current.includes(raumIndex)) {
      updated[agIndex].zugeordnete_raeume = current.filter(i => i !== raumIndex);
    } else {
      // Bei Single nur 1 Raum erlauben
      if (updated[agIndex].typ === 'Single') {
        updated[agIndex].zugeordnete_raeume = [raumIndex];
      } else {
        updated[agIndex].zugeordnete_raeume = [...current, raumIndex];
      }
    }
    
    setAussengeraete(updated);
  };

  // Image handling moved to ImageUploader component
  const handleImagesChange = (newImages) => {
    setBilder(newImages);
  };

  const handleSubmit = async (status) => {
    if (!customer) {
      setError('Kein Kunde ausgewählt. Bitte zurück zur Kundenauswahl.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Konvertiere Strings zu Numbers für Dimensionen
      const formattedRaeume = raeume.map(raum => ({
        ...raum,
        laenge: parseNumber(raum.laenge),
        breite: parseNumber(raum.breite),
        hoehe: parseNumber(raum.hoehe),
        kuehlleistung_kw: parseFloat(calculateKuehlleistung(raum)),
        leitungslaenge_innen_aussen: parseNumber(raum.leitungslaenge_innen_aussen)
      }));
      
      const formattedAussengeraete = aussengeraete.map(ag => {
        // Berechne gesamt_leitungslaenge aus zugeordneten Räumen
        let gesamt = 0;
        ag.zugeordnete_raeume.forEach(raumIdx => {
          if (raumIdx >= 0 && raumIdx < raeume.length) {
            gesamt += parseNumber(raeume[raumIdx].leitungslaenge_innen_aussen);
          }
        });
        
        return {
          ...ag,
          hoehe: parseNumber(ag.hoehe || '0'),
          gesamt_leitungslaenge: gesamt,
          vollwaermeschutz_staerke_mm: parseNumber(ag.vollwaermeschutz_staerke_mm || '0')
        };
      });
      
      const data = {
        ku_nr: customer.ku_nr || '',
        kunde_name: customer.name || customer.kunde_name || '',
        kunde_strasse: customer.strasse || customer.kunde_strasse || '',
        kunde_plz: customer.plz || customer.kunde_plz || '',
        kunde_ort: customer.ort || customer.kunde_ort || '',
        kunde_telefon: customer.mobilfunk || customer.festnetz || customer.kunde_telefon || '',
        kunde_email: customer.email || customer.kunde_email || '',
        monteur,
        datum,
        hersteller_wunsch: herstellerWunsch,
        hersteller_empfehlung: herstellerEmpfehlung,
        serviceart,
        montage_geplant: montageGeplant,
        raeume: formattedRaeume,
        aussengeraete: formattedAussengeraete,
        kernbohrungen,
        bohrungen,
        mauerschlitz, // NEU
        bilder,
        notizen,
        status
      };

      // DEBUG: Log bilder data before sending
      console.log('=== FRONTEND DEBUG - Sending Angebot ===');
      console.log('Bilder count:', bilder.length);
      console.log('Full data object:', JSON.stringify(data, null, 2).substring(0, 2000)); // First 2000 chars
      if (bilder.length > 0) {
        bilder.forEach((img, idx) => {
          console.log(`Bild ${idx}:`, {
            name: img.name,
            hasDescription: !!img.description,
            description: img.description,
            hasData: !!img.data,
            dataLength: img.data ? img.data.length : 0,
            mime_type: img.mime_type
          });
        });
      }
      console.log('=======================================');

      let response;
      if (angebotId) {
        // UPDATE
        response = await axios.put(`${API_URL}/angebot/${angebotId}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // CREATE
        response = await axios.post(`${API_URL}/angebot`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      alert(status === 'draft' ? 'Entwurf gespeichert' : 'Angebot versendet!');
      navigate('/angebot-list');
      
    } catch (err) {
      console.error('Error saving angebot:', err);
      console.error('Error response:', err.response);
      console.error('Error detail:', err.response?.data?.detail);
      
      let errorMessage = 'Fehler beim Speichern: ';
      if (err.response?.data?.detail) {
        // Backend validation error
        if (Array.isArray(err.response.data.detail)) {
          const detailMessages = err.response.data.detail.map(e => {
            const location = e.loc ? e.loc.join('.') : 'unknown';
            return `${location}: ${e.msg}`;
          });
          errorMessage += detailMessages.join('\n');
          console.error('Validation errors:', detailMessages);
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage += err.response.data.detail;
        } else {
          errorMessage += JSON.stringify(err.response.data.detail);
        }
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Unbekannter Fehler';
      }
      
      setError(errorMessage);
      alert(errorMessage); // Auch als Alert anzeigen
    } finally {
      setSaving(false);
    }
  };

  if (!customer) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppHeader title="Angebot erstellen" showBack={true} backTo="/angebot/customer-search" />
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">Kein Kunde ausgewählt. Bitte zurück zur Kundenauswahl.</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppHeader title="Angebot erstellen" showBack={true} backTo="/angebot-list" />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Kundendaten */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Kundendaten</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography><strong>Name:</strong> {customer.name || customer.kunde_name}</Typography>
              <Typography><strong>Adresse:</strong> {customer.strasse || customer.kunde_strasse}, {customer.plz || customer.kunde_plz} {customer.ort || customer.kunde_ort}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography><strong>Telefon:</strong> {customer.mobilfunk || customer.festnetz}</Typography>
              <Typography><strong>Email:</strong> {customer.email}</Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Grunddaten */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Angebotsdaten</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Monteur" value={monteur} onChange={(e) => setMonteur(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="date" label="Datum" value={datum} onChange={(e) => setDatum(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Wunschhersteller Kunde</InputLabel>
                <Select value={herstellerWunsch} onChange={(e) => setHerstellerWunsch(e.target.value)} sx={{ minWidth: '120px' }}>
                  <MenuItem value="">Keine Angabe</MenuItem>
                  {HERSTELLER.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Empfehlung Monteur</InputLabel>
                <Select value={herstellerEmpfehlung} onChange={(e) => setHerstellerEmpfehlung(e.target.value)} sx={{ minWidth: '120px' }}>
                  <MenuItem value="">Keine Angabe</MenuItem>
                  {HERSTELLER.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <Typography variant="body2" gutterBottom>Serviceart:</Typography>
                <RadioGroup row value={serviceart} onChange={(e) => setServiceart(e.target.value)}>
                  <FormControlLabel value="Komplettmontage" control={<Radio />} label="Komplettmontage" />
                  <FormControlLabel value="Inbetriebnahmeservice" control={<Radio />} label="Inbetriebnahmeservice" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Montage geplant</InputLabel>
                <Select value={montageGeplant} onChange={(e) => setMontageGeplant(e.target.value)}>
                  <MenuItem value="sofort">Sofort</MenuItem>
                  <MenuItem value="1 bis 3 Monate">1 bis 3 Monate</MenuItem>
                  <MenuItem value="bis 6 Monate">Bis 6 Monate</MenuItem>
                  <MenuItem value="später">Später</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Räume */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Innengeräte / Räume</Typography>

          {raeume.map((raum, idx) => (
            <Accordion 
              key={idx} 
              sx={{ mb: 2 }}
              expanded={expandedRaeume.has(idx)}
              onChange={handleRaumAccordionChange(idx)}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography><strong>Raum {idx + 1}:</strong> {raum.raumname || 'Ohne Namen'}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField 
                      fullWidth 
                      label="Raumname" 
                      value={raum.raumname} 
                      onChange={(e) => updateRaum(idx, 'raumname', capitalize(e.target.value))} 
                      id={`raum-${idx}-raumname`}
                    />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <TextField fullWidth label="Länge (m)" type="text" value={raum.laenge} onChange={(e) => updateRaum(idx, 'laenge', e.target.value)} placeholder="z.B. 5,5" />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <TextField fullWidth label="Breite (m)" type="text" value={raum.breite} onChange={(e) => updateRaum(idx, 'breite', e.target.value)} placeholder="z.B. 4,0" />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <TextField fullWidth label="Höhe (m)" type="text" value={raum.hoehe} onChange={(e) => updateRaum(idx, 'hoehe', e.target.value)} placeholder="z.B. 2,5" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Gerätetyp</InputLabel>
                      <Select value={raum.geraetetyp} onChange={(e) => updateRaum(idx, 'geraetetyp', e.target.value)}>
                        {GERAETETYPEN.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* NEU: Isolierstandard & Kühllast */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Isolierstandard</InputLabel>
                      <Select
                        value={raum.isolierstandard}
                        label="Isolierstandard"
                        onChange={(e) => updateRaum(idx, 'isolierstandard', e.target.value)}
                      >
                        <MenuItem value="neubau">Neubau (30 W/m³)</MenuItem>
                        <MenuItem value="altbau">Altbau (45 W/m³)</MenuItem>
                        <MenuItem value="dach">Dach (65 W/m³)</MenuItem>
                        <MenuItem value="wintergarten">Wintergarten (85 W/m³)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      fullWidth 
                      label="Kühllast (kW)" 
                      value={calculateKuehlleistung(raum)} 
                      InputProps={{ readOnly: true }}
                      sx={{ bgcolor: '#fffacd' }}
                      helperText={`Berechnet: ${calculateVolumen(raum)} m³ × ${(() => {
                        const wattProKubikmeter = {
                          'neubau': 30,
                          'altbau': 45,
                          'dach': 65,
                          'wintergarten': 85
                        };
                        return wattProKubikmeter[raum.isolierstandard] || 45;
                      })()} W/m³`}
                    />
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <TextField fullWidth label="m²" value={calculateFlaeche(raum)} InputProps={{ readOnly: true }} sx={{ bgcolor: '#f0f0f0' }} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth label="m³" value={calculateVolumen(raum)} InputProps={{ readOnly: true }} sx={{ bgcolor: '#f0f0f0' }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Leitungslänge Innen-Außen (m)" type="text" value={raum.leitungslaenge_innen_aussen} onChange={(e) => updateRaum(idx, 'leitungslaenge_innen_aussen', e.target.value)} />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Zusatzoptionen:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <FormControlLabel 
                        control={<Checkbox checked={raum.kondenswasserpumpe} onChange={(e) => updateRaum(idx, 'kondenswasserpumpe', e.target.checked)} />} 
                        label="Kondenswasserpumpe" 
                        sx={{ 
                          bgcolor: raum.kondenswasserpumpe ? '#c8e6c9' : 'transparent',
                          borderRadius: 1,
                          px: 1,
                          mr: 1
                        }}
                      />
                      <FormControlLabel 
                        control={<Checkbox checked={raum.wifi} onChange={(e) => updateRaum(idx, 'wifi', e.target.checked)} />} 
                        label="WiFi-Modul" 
                        sx={{ 
                          bgcolor: raum.wifi ? '#c8e6c9' : 'transparent',
                          borderRadius: 1,
                          px: 1
                        }}
                      />
                      <TextField label="Wunschfarbe" value={raum.wunschfarbe} onChange={(e) => updateRaum(idx, 'wunschfarbe', e.target.value)} size="small" sx={{ width: '200px' }} />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={2} label="Besonderheiten" value={raum.besonderheiten} onChange={(e) => updateRaum(idx, 'besonderheiten', e.target.value)} />
                  </Grid>

                  <Grid item xs={12}>
                    <Button startIcon={<Remove />} onClick={() => removeRaum(idx)} color="error" disabled={raeume.length === 1}>Raum entfernen</Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}

          <Box sx={{ mt: 2 }}>
            <Button startIcon={<Add />} onClick={addRaum} variant="outlined">Raum hinzufügen</Button>
          </Box>
        </Paper>

        {/* Außengeräte */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Außengeräte</Typography>

          {aussengeraete.map((ag, idx) => (
            <Accordion 
              key={idx} 
              sx={{ mb: 2 }}
              expanded={expandedAussengeraete.has(idx)}
              onChange={handleAussengeraetAccordionChange(idx)}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography><strong>Außengerät {idx + 1}:</strong> {ag.typ}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Typ</InputLabel>
                      <Select value={ag.typ} onChange={(e) => updateAussengeraet(idx, 'typ', e.target.value)}>
                        <MenuItem value="Single">Single</MenuItem>
                        <MenuItem value="Multi">Multi</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Montageort</InputLabel>
                      <Select 
                        value={ag.montageort} 
                        onChange={(e) => updateAussengeraet(idx, 'montageort', e.target.value)}
                        id={`aussengeraet-${idx}-montageort`}
                      >
                        <MenuItem value="Wand">Wand</MenuItem>
                        <MenuItem value="Boden">Boden</MenuItem>
                        <MenuItem value="Dach">Dach</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Höhe (m)" type="text" value={ag.hoehe} onChange={(e) => updateAussengeraet(idx, 'hoehe', e.target.value)} />
                  </Grid>

                  {/* Gesamtleitungslänge (berechnet) */}
                  <Grid item xs={12} md={4}>
                    <TextField 
                      fullWidth 
                      label="Gesamtleitungslänge (m)" 
                      value={(() => {
                        let gesamt = 0;
                        ag.zugeordnete_raeume.forEach(raumIdx => {
                          if (raumIdx >= 0 && raumIdx < raeume.length) {
                            gesamt += parseNumber(raeume[raumIdx].leitungslaenge_innen_aussen);
                          }
                        });
                        return gesamt.toFixed(2);
                      })()}
                      InputProps={{ readOnly: true }}
                      sx={{ bgcolor: '#f0f0f0' }}
                      helperText="Automatisch berechnet"
                    />
                  </Grid>

                  {/* Gesamtkälteleistung (berechnet) */}
                  <Grid item xs={12} md={4}>
                    <TextField 
                      fullWidth 
                      label="Gesamtkälteleistung (kW)" 
                      value={(() => {
                        let gesamt = 0;
                        ag.zugeordnete_raeume.forEach(raumIdx => {
                          if (raumIdx >= 0 && raumIdx < raeume.length) {
                            gesamt += parseNumber(raeume[raumIdx].kuehlleistung_kw);
                          }
                        });
                        return gesamt.toFixed(2);
                      })()}
                      InputProps={{ readOnly: true }}
                      sx={{ bgcolor: '#fff9c4', fontWeight: 'bold' }}
                      helperText="Summe aller zugeordneten Räume"
                    />
                  </Grid>

                  {/* NEU: Montage-Auswahl auch bei Single */}
                  <Grid item xs={12} md={12}>
                    <FormControl fullWidth>
                      <InputLabel>Montage auf</InputLabel>
                      <Select value={ag.montage_auf} onChange={(e) => updateAussengeraet(idx, 'montage_auf', e.target.value)} sx={{ minWidth: '150px' }}>
                        <MenuItem value="">Keine Angabe</MenuItem>
                        {MONTAGE_AUF.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Raumzuordnung mit intelligenter Filterung */}
                  <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom><strong>Zugeordnete Räume:</strong> {ag.typ === 'Single' ? '(maximal 1)' : '(mehrere möglich)'}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {getAvailableRaeume(idx).map((raum) => (
                        <Chip
                          key={raum.index}
                          label={raum.name}
                          onClick={() => !raum.disabled && toggleRaumZuordnung(idx, raum.index)}
                          color={ag.zugeordnete_raeume.includes(raum.index) ? 'primary' : 'default'}
                          disabled={raum.disabled}
                          sx={{ cursor: raum.disabled ? 'not-allowed' : 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Checkboxen */}
                  <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom><strong>Zusatzoptionen:</strong></Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <FormControlLabel control={<Checkbox checked={ag.dachtritt_vorhanden} onChange={(e) => updateAussengeraet(idx, 'dachtritt_vorhanden', e.target.checked)} />} label="Dachtritt vorhanden" sx={{ bgcolor: ag.dachtritt_vorhanden ? '#c8e6c9' : 'transparent', borderRadius: 1, px: 1 }} />
                      <FormControlLabel control={<Checkbox checked={ag.kondenswasserwanne} onChange={(e) => updateAussengeraet(idx, 'kondenswasserwanne', e.target.checked)} />} label="Kondenswasserwanne" sx={{ bgcolor: ag.kondenswasserwanne ? '#c8e6c9' : 'transparent', borderRadius: 1, px: 1 }} />
                      <FormControlLabel control={<Checkbox checked={ag.oelauffangwanne} onChange={(e) => updateAussengeraet(idx, 'oelauffangwanne', e.target.checked)} />} label="Ölauffangwanne" sx={{ bgcolor: ag.oelauffangwanne ? '#c8e6c9' : 'transparent', borderRadius: 1, px: 1 }} />
                      <FormControlLabel control={<Checkbox checked={ag.einhausung} onChange={(e) => updateAussengeraet(idx, 'einhausung', e.target.checked)} />} label="Einhausung" sx={{ bgcolor: ag.einhausung ? '#c8e6c9' : 'transparent', borderRadius: 1, px: 1 }} />
                      <FormControlLabel control={<Checkbox checked={ag.ablaufheizung} onChange={(e) => updateAussengeraet(idx, 'ablaufheizung', e.target.checked)} />} label="Ablaufheizung" sx={{ bgcolor: ag.ablaufheizung ? '#c8e6c9' : 'transparent', borderRadius: 1, px: 1 }} />
                      <FormControlLabel control={<Checkbox checked={ag.vollwaermeschutz} onChange={(e) => updateAussengeraet(idx, 'vollwaermeschutz', e.target.checked)} />} label="Vollwärmeschutz" sx={{ bgcolor: ag.vollwaermeschutz ? '#c8e6c9' : 'transparent', borderRadius: 1, px: 1 }} />
                    </Box>
                  </Grid>

                  {/* Vollwärmeschutz Stärke - nur anzeigen wenn aktiviert */}
                  {ag.vollwaermeschutz && (
                    <Grid item xs={12} md={6}>
                      <TextField 
                        fullWidth 
                        label="Vollwärmeschutz Stärke (mm)" 
                        type="text" 
                        value={ag.vollwaermeschutz_staerke_mm} 
                        onChange={(e) => updateAussengeraet(idx, 'vollwaermeschutz_staerke_mm', e.target.value)} 
                        placeholder="z.B. 120"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom><strong>Logistik:</strong></Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <FormControlLabel control={<Checkbox checked={ag.montagelift} onChange={(e) => updateAussengeraet(idx, 'montagelift', e.target.checked)} />} label="Montagelift notwendig" sx={{ bgcolor: ag.montagelift ? '#c8e6c9' : 'transparent', borderRadius: 1, px: 1 }} />
                      <FormControlLabel control={<Checkbox checked={ag.arbeitsbuehne} onChange={(e) => updateAussengeraet(idx, 'arbeitsbuehne', e.target.checked)} />} label="Arbeitsbühne notwendig" sx={{ bgcolor: ag.arbeitsbuehne ? '#c8e6c9' : 'transparent', borderRadius: 1, px: 1 }} />
                      <FormControlLabel control={<Checkbox checked={ag.dachlift} onChange={(e) => updateAussengeraet(idx, 'dachlift', e.target.checked)} />} label="Dachlift notwendig" sx={{ bgcolor: ag.dachlift ? '#c8e6c9' : 'transparent', borderRadius: 1, px: 1 }} />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Button startIcon={<Remove />} onClick={() => removeAussengeraet(idx)} color="error" disabled={aussengeraete.length === 1}>Außengerät entfernen</Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}

          <Box sx={{ mt: 2 }}>
            <Button startIcon={<Add />} onClick={addAussengeraet} variant="outlined">Außengerät hinzufügen</Button>
          </Box>
        </Paper>

        {/* Bohrungen & Mauerschlitz */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Bohrungen & Mauerschlitz</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Kernbohrungen" value={kernbohrungen} onChange={(e) => setKernbohrungen(e.target.value)} placeholder="z.B. 2x Durchmesser 80mm" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Bohrungen" value={bohrungen} onChange={(e) => setBohrungen(e.target.value)} placeholder="z.B. 4x Standard" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Mauerschlitz (m)" type="text" value={mauerschlitz} onChange={(e) => setMauerschlitz(e.target.value)} placeholder="z.B. 5,5" />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Bilder */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Fotos (max. 10) {bilder.length > 0 && `- ${bilder.length} hochgeladen`}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ImageUploader 
              images={bilder}
              onImagesChange={handleImagesChange}
              maxImages={10}
            />
          </AccordionDetails>
        </Accordion>

        {/* Notizen */}
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Notizen</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField fullWidth multiline rows={4} value={notizen} onChange={(e) => setNotizen(e.target.value)} placeholder="Zusätzliche Hinweise..." />
          </AccordionDetails>
        </Accordion>

        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button variant="outlined" startIcon={<Save />} onClick={() => handleSubmit('draft')} disabled={saving}>
            Als Entwurf speichern
          </Button>
          <Button variant="contained" startIcon={<Send />} onClick={() => handleSubmit('submitted')} disabled={saving}>
            SPEICHERN & SENDEN
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
